"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  RadioRows,
  RadioRowsItem,
  RadioRowsItemDescription,
  RadioRowsItemTitle,
} from "@/components/ui/radio-rows";
import { useEliteStructure } from "@/contexts/EliteTemporaryContextProvider";
import type { WebCheckInReservationData } from "@/types/Reservation";
import {
  Briefcase,
  CalendarCheck,
  CheckCircle2,
  CircleUserRound,
  Ellipsis,
  Hotel,
  Loader2,
  LogIn,
  LogOut,
  Send,
  Sparkles,
  TreePalm,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface ReservationSummaryCardProps {
  reservationData: WebCheckInReservationData;
  visitReason: string;
  onVisitReasonChange: (value: string) => void;
  pendingDraftCount: number;
  totalRegisteredCount: number;
  totalExpectedCount: number;
  loading: boolean;
  onSubmitCheckIn: () => void;
  className?: string;
}

export function ReservationSummaryCard({
  reservationData,
  visitReason,
  onVisitReasonChange,
  pendingDraftCount,
  totalRegisteredCount,
  totalExpectedCount,
  loading,
  onSubmitCheckIn,
  className,
}: ReservationSummaryCardProps) {
  const { t } = useTranslation("checkIn");
  const { structure } = useEliteStructure();
  const res = reservationData?.reservation;
  const guest = reservationData?.guest;

  const totalEffective = totalRegisteredCount + pendingDraftCount;
  const progressPercent =
    totalExpectedCount > 0
      ? Math.min(100, Math.round((totalEffective / totalExpectedCount) * 100))
      : 0;

  const arrivalDate = res?.arrival ? new Date(res.arrival) : null;
  const departureDate = res?.departure ? new Date(res.departure) : null;
  const nights =
    arrivalDate && departureDate
      ? Math.max(
          1,
          Math.round(
            (departureDate.getTime() - arrivalDate.getTime()) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : 1;

  const reasons = [
    {
      value: "1",
      label: t("reasons.vacation", "Vacanza / Tempo Libero"),
      desc: t(
        "reasons.vacationDesc",
        "Soggiorno per motivi personali, relax o vacanza",
      ),
      icon: <TreePalm className="size-4 text-emerald-500" />,
    },
    {
      value: "2",
      label: t("reasons.work", "Lavoro / Business"),
      desc: t(
        "reasons.workDesc",
        "Soggiorno per affari, convegni o attività professionali",
      ),
      icon: <Briefcase className="size-4 text-blue-500" />,
    },
    {
      value: "3",
      label: t("reasons.other", "Altro motivo"),
      desc: t("reasons.otherDesc", "Studio, salute o altri motivi"),
      icon: <Sparkles className="size-4 text-amber-500" />,
    },
  ];

  return (
    <Card
      className={`overflow-hidden border border-border/70 shadow-sm py-0 gap-0 ${className || ""}`}
    >
      {/* Hotel & Reservation Header */}
      <CardHeader className="bg-muted/30 border-b p-4 sm:p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {structure?.logo ? (
              <img
                src={structure.logo}
                alt={structure.name || "Hotel"}
                className="max-h-8 max-w-[120px] object-contain"
              />
            ) : (
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Hotel className="size-4" />
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                {t("summary.title", "Riepilogo Soggiorno")}
              </p>
              <h3 className="text-sm font-bold text-foreground line-clamp-1">
                {structure?.name || t("summary.hotel", "Struttura Ricettiva")}
              </h3>
            </div>
          </div>

          <Badge variant="outline" className="font-mono text-xs px-2.5 py-0.5">
            #{res?.idReservationPms || res?.id}
          </Badge>
        </div>

        {/* Guest Holder */}
        {(guest?.name || res?.name) && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-background border text-xs">
            <CircleUserRound className="size-4 text-primary shrink-0" />
            <div className="truncate">
              <span className="text-muted-foreground">
                {t("summary.holder", "Intestatario:")}{" "}
              </span>
              <span className="font-semibold text-foreground">
                {guest?.name || res?.name} {guest?.surname || res?.surname}
              </span>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="p-4 sm:p-5 space-y-5">
        {/* Dates & Nights */}
        <div className="grid grid-cols-2 gap-2 p-3 rounded-xl bg-muted/20 border text-xs">
          <div className="space-y-1">
            <span className="text-muted-foreground flex items-center gap-1">
              <LogIn className="size-3 text-emerald-500" />
              {t("summary.checkIn", "Check-in")}
            </span>
            <p className="font-semibold text-foreground text-sm">
              {arrivalDate ? arrivalDate.toLocaleDateString() : "-"}
            </p>
          </div>

          <div className="space-y-1 border-l pl-3">
            <span className="text-muted-foreground flex items-center gap-1">
              <LogOut className="size-3 text-amber-500" />
              {t("summary.checkOut", "Check-out")}
            </span>
            <p className="font-semibold text-foreground text-sm">
              {departureDate ? departureDate.toLocaleDateString() : "-"}
            </p>
          </div>

          <div className="col-span-2 pt-2 border-t mt-1 flex items-center justify-between text-muted-foreground text-[11px]">
            <span>{t("summary.duration", "Durata soggiorno:")}</span>
            <span className="font-semibold text-foreground">
              {t(nights === 1 ? "summary.night_one" : "summary.night_other", {
                count: nights,
                defaultValue: `${nights} ${nights === 1 ? "notte" : "notti"}`,
              })}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-foreground flex items-center gap-1.5">
              <CalendarCheck className="size-4 text-primary" />
              {t("summary.progress", "Avanzamento Check-in")}
            </span>
            <span className="font-semibold text-primary">
              {t("ofInserted", {
                current: totalEffective,
                total: totalExpectedCount,
                defaultValue: `${totalEffective} di ${totalExpectedCount}`,
              })}{" "}
              ({progressPercent}%)
            </span>
          </div>

          <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {pendingDraftCount > 0 ? (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
              {t(
                pendingDraftCount === 1 ? "newReady_one" : "newReady_other",
                {
                  count: pendingDraftCount,
                  defaultValue: `${pendingDraftCount} ${pendingDraftCount === 1 ? "nuovo pronto" : "nuovi pronti"}`,
                },
              )}
            </p>
          ) : totalRegisteredCount >= totalExpectedCount ? (
            <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
              <CheckCircle2 className="size-3" />
              {t("allRegistered", "Tutti gli ospiti previsti sono registrati!")}
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              {t("summary.missingGuests_other", {
                count: totalExpectedCount - totalRegisteredCount,
                defaultValue: `Mancano ${totalExpectedCount - totalRegisteredCount} ospiti da inserire.`,
              })}
            </p>
          )}
        </div>

        {/* Motivo Soggiorno */}
        <div className="space-y-2.5 pt-2 border-t">
          <Label className="text-xs font-semibold text-foreground uppercase tracking-wider">
            {t("summary.stayReason", "Motivo del Soggiorno")}
          </Label>

          <RadioRows
            value={visitReason}
            onValueChange={onVisitReasonChange}
            className="gap-1.5"
          >
            {reasons.map((r) => (
              <RadioRowsItem
                key={r.value}
                value={r.value}
                variant="secondary"
                className="py-2.5 px-3 rounded-lg border text-left"
              >
                <RadioRowsItemTitle className="flex items-center gap-2 text-xs font-medium">
                  {r.icon}
                  {r.label}
                </RadioRowsItemTitle>
                <RadioRowsItemDescription className="text-[11px] text-muted-foreground pl-6">
                  {r.desc}
                </RadioRowsItemDescription>
              </RadioRowsItem>
            ))}
          </RadioRows>
        </div>

        {/* Submit Button (Desktop View) */}
        <div className="pt-2 hidden md:block">
          <Button
            type="button"
            size="lg"
            className="w-full py-6 text-sm font-bold shadow-md gap-2 cursor-pointer"
            disabled={pendingDraftCount === 0 || loading}
            onClick={onSubmitCheckIn}
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("submitting", "Invio Check-in in corso...")}
              </>
            ) : (
              <>
                <Send className="size-4" />
                {pendingDraftCount > 0
                  ? `${t("submitButton", "Invia Check-in")} (${pendingDraftCount})`
                  : totalRegisteredCount >= totalExpectedCount
                    ? t("checkInComplete", "Check-in già completato")
                    : t("summary.addAtLeastOneBtn", "Inserisci almeno un ospite")}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
